import { useState } from 'react';
import { ArrowSquareOut, Copy, GoogleLogo, MapPin, NavigationArrow } from '@phosphor-icons/react';
import { InnenKarte, LeerZustand, SegmentedControl, ToastPill, useToastPill } from '../../../../../components/immobilien/ui';
import { googleMapsLink, kartenEinbettung, osmLink, routeLink, type Kartentyp } from '../../../../../lib/immobilien/karte';
import { useObjektSeite } from '../ObjektSeitenKontext';

const ZOOMS = [
  { key: '17', label: 'Straße' },
  { key: '15', label: 'Viertel' },
  { key: '13', label: 'Stadt' },
  { key: '11', label: 'Region' },
] as const;

const linkStil = {
  height: 40, padding: '0 16px', borderRadius: 20, gap: 7, fontSize: 13, fontWeight: 700, textDecoration: 'none',
} as const;

/** Panel „Lage & Karte“ (Google Maps). */
export function KartePanel() {
  const { adresse } = useObjektSeite();
  const [zoom, setZoom] = useState<typeof ZOOMS[number]['key']>('15');
  const [typ, setTyp] = useState<Kartentyp>('roadmap');
  const [toast, zeige] = useToastPill();

  if (!adresse) {
    return <LeerZustand icon={MapPin} titel="Keine Adresse hinterlegt" text="Trage Straße, PLZ und Ort unter Technik & Stammdaten → Bearbeiten ein, dann erscheint hier die Karte." />;
  }

  const kopiere = async () => {
    try { await navigator.clipboard.writeText(adresse); zeige('Adresse kopiert'); } catch { zeige('Kopieren nicht möglich', true); }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <div className="flex flex-wrap items-start" style={{ gap: 14 }}>
        <div className="flex flex-col min-w-0" style={{ flex: '2 1 460px', gap: 10 }}>
          <div className="relative overflow-hidden" style={{ height: 440, borderRadius: 26, background: 'var(--fill2)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
            <iframe src={kartenEinbettung(adresse, Number(zoom), typ)} title={`Karte: ${adresse}`} loading="lazy"
              className="absolute inset-0" style={{ width: '100%', height: '100%', border: 0 }} referrerPolicy="no-referrer-when-downgrade" />
            <div className="absolute flex" style={{ top: 12, left: 12, gap: 6 }}>
              {([['roadmap', 'Karte'], ['satellite', 'Satellit']] as const).map(([k, l]) => (
                <button key={k} type="button" aria-pressed={typ === k} onClick={() => setTyp(k)} className="cursor-pointer"
                  style={{ height: 30, padding: '0 12px', borderRadius: 15, border: 'none', fontSize: 12, fontWeight: 800, background: typ === k ? 'var(--acc-fill)' : 'var(--chip)', color: typ === k ? 'var(--acc-ink)' : 'var(--chip-ink)', backdropFilter: 'blur(14px)', boxShadow: '0 4px 14px -6px var(--shade)' }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <SegmentedControl segmente={ZOOMS} wert={zoom} onChange={setZoom} hoehe={30} voll ariaLabel="Zoom" />
        </div>
        <div className="flex flex-col min-w-0" style={{ flex: '1 1 260px', gap: 12 }}>
          <InnenKarte>
            <div className="flex items-center" style={{ gap: 12 }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: 'var(--acc-soft)' }}>
                <MapPin weight="fill" size={20} color="var(--acc)" />
              </span>
              <span className="flex-1 min-w-0" style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>{adresse}</span>
              <button type="button" title="Adresse kopieren" aria-label="Adresse kopieren" onClick={() => { void kopiere(); }} className="immo-hover-flaeche flex items-center justify-center cursor-pointer"
                style={{ width: 34, height: 34, borderRadius: 11, border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}>
                <Copy size={16} />
              </button>
            </div>
            <div className="flex flex-col" style={{ gap: 8 }}>
              <a href={routeLink(adresse)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center"
                style={{ ...linkStil, background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
                <NavigationArrow weight="bold" size={15} />Route planen
              </a>
              <div className="flex" style={{ gap: 8 }}>
                <a href={googleMapsLink(adresse)} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center"
                  style={{ ...linkStil, background: 'var(--fill2)', color: 'var(--ink)' }}>
                  <GoogleLogo weight="bold" size={15} />Google Maps
                </a>
                <a href={osmLink(adresse)} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center"
                  style={{ ...linkStil, background: 'var(--fill2)', color: 'var(--ink)' }}>
                  <ArrowSquareOut weight="bold" size={15} />Große Karte
                </a>
              </div>
            </div>
          </InnenKarte>
        </div>
      </div>
    </>
  );
}
